

// React Flow specific types
import { Node, Edge } from 'reactflow';
import { ReactNode } from 'react';

export enum ResourceKind {
  Single = 'Single',
  Collection = 'Collection',
  Fixed = 'Fixed'
}

export enum Direction {
  BottomUp = 0,
  TopDown = 1
}

export enum ApprovalGroupType {
  Room = 'Room',
  Area = 'Area',
  Global = 'Global'
}

export interface Membership {
  groupName: string;
  nodeName: string;
  role: string;
}

export interface EntityConfiguration {
  [key: string]: unknown;
}

// Entity Definitions
export interface ResourceEntity {
  id: string; // Internal ID (matches resourceId in Ref)
  name: string;
  description: string;
  referenceId: string; // External System ID
  resourceKind: ResourceKind;
  resourceType: string;
  memberships: Membership[];
  configuration: EntityConfiguration;
  systemId: string;
}

export interface ResourcePlanEntity {
  id: string; // Matches resourcePlanId in Ref
  name: string;
  description: string;
  referenceId: string; // External System ID
  priority: number;
  memberships: Membership[];
  configuration: EntityConfiguration;
  systemId: string;
}

export interface SystemEntity {
  id: string; // Matches systemId in Ref
  name: string;
  memberships: Membership[];
  resources: ResourceEntity[];
  resourcePlans: ResourcePlanEntity[];
}

export interface ResourceRef {
  systemId: string;
  resourceId: string;
  resourcePlanId?: string | null;
}

export interface InheritedResource extends ResourceRef {
  sourceNodeId: string;
  sourceNodeName: string;
  sourceNodePriority: number;
  isInherited: boolean;
}

export interface LocationGroup {
  groupId: string;
  nodeId: string;
}

export interface ApprovalGroup {
  groupId: string;
  nodeId: string;
  type: ApprovalGroupType | string;
}

export interface WorkflowConfig {
  type: string;
  roomManagerGroup?: string;
}

export interface ScheduleConfig {
  workflow?: WorkflowConfig;
  [key: string]: unknown;
}

export interface PhaseOutDefinition {
  scheduleId: string;
  duration: string;
}

export interface Schedule {
  scheduleId: string;
  isActive: boolean;
  name: string;
  time: string;
  group: string;
  purpose: string;
  priority: number;
  locationGroup: LocationGroup;
  approvalGroups: ApprovalGroup[];
  direction: Direction; // 0 = Bottom Up (Parents), 1 = Top Down (Children)
  tags: string[];
  attributes: string[] | null;
  configuration: ScheduleConfig;
  securityLevelId: string | null;
  followUp: string | null;
  phaseOut: PhaseOutDefinition | null;
  resources: ResourceRef[];
  _inheritedResources?: InheritedResource[]; // Runtime computed property
  children: string[];
  parents: string[];
  requirements: unknown[];
}

export interface EntitlementObject {
  objectType: string;
  name: string;
  tags: string[];
  configuration: EntityConfiguration;
  schedules: Schedule[];
}

export type EntitlementNode = Node<Schedule>;
export type EntitlementEdge = Edge;

// --- Context Menu Types ---

export interface ContextMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: (...args: unknown[]) => void;
  disabled?: boolean;
  danger?: boolean;
}

// --- Repository Interfaces (Async/REST Contracts) ---

export interface QueryParams {
  page?: number;
  pageSize?: number;
  filter?: string;
  sortBy?: string;
}

export interface QueryResult<T> {
  items: T[];
  total: number;
}

export interface HierarchyItem {
  id: string; // NodeId
  groupId: string; // GroupId
  name: string;
  type: 'Location' | 'Approval';
}

export interface IRepositories {
  // Async list fetchers for dropdowns and search
  getSystems: (params?: QueryParams) => Promise<QueryResult<SystemEntity>>;
  getResources: (params?: QueryParams) => Promise<QueryResult<ResourceEntity>>;
  getPlans: (params?: QueryParams) => Promise<QueryResult<ResourcePlanEntity>>;
  getLocations: (params?: QueryParams) => Promise<QueryResult<HierarchyItem>>;
  getApprovalGroups: (params?: QueryParams) => Promise<QueryResult<HierarchyItem>>;
  
  // Async Batch Resolvers for displaying details
  resolveSystems: (ids: string[]) => Promise<Map<string, SystemEntity>>;
  resolveResources: (ids: string[]) => Promise<Map<string, ResourceEntity>>;
  resolvePlans: (ids: string[]) => Promise<Map<string, ResourcePlanEntity>>;
  resolveLocations: (ids: string[]) => Promise<Map<string, string>>; // Keep as simple string map for location names
}

// --- Schedule Group Definitions ---

export interface ScheduleGroupDefinition {
  name: string;
  color?: string;   // tailwind color key: 'red' | 'blue' | 'purple' | 'amber' | 'indigo' | 'orange' | 'slate' | 'emerald' | 'teal' | 'pink'
  icon?: string;     // lucide icon name: 'AlertTriangle' | 'Crown' | 'Clock' | 'Moon' | 'ShieldAlert' | 'Wrench' | etc.
}

export const DEFAULT_SCHEDULE_GROUPS: ScheduleGroupDefinition[] = [
  { name: 'Standard', color: 'slate' },
  { name: 'Emergency', color: 'red', icon: 'AlertTriangle' },
  { name: 'VIP', color: 'purple', icon: 'Crown' },
  { name: 'Temporary', color: 'amber', icon: 'Clock' },
  { name: 'Night Shift', color: 'indigo', icon: 'Moon' },
  { name: 'Restricted', color: 'orange', icon: 'ShieldAlert' },
  { name: 'Maintenance', color: 'blue', icon: 'Wrench' },
];

// --- Component Props ---

export interface EntitlementEditorProps {
  initialData: EntitlementObject;
  // Data dependencies - pass directly to avoid context issues with CDN
  repositories?: IRepositories;
  // Lifecycle & Persistence
  onSave?: (data: EntitlementObject) => void;
  onSaveAndReturn?: (data: EntitlementObject) => void;
  onBack?: () => void;
  
  // Customization Hooks
  additionalCanvasMenuItems?: ContextMenuItem[];
  additionalNodeMenuItems?: (node: Schedule) => ContextMenuItem[];
}